import React, {useState} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {useForm, Controller} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from "zod";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useAuth} from "@/auth/useAuth";
import {ErrorMessage} from "@/components/common/ErrorMessage";
import {TextInput} from "@/components/form/TextInput";
import {SubmitButton} from "@/components/form/SubmitButton";
import {TextButton} from "@/components/form/TextButton";
import {ThemeType} from "@/theming/baseTheme";
import {Card} from "@/components/common/Card";
import {AnimatedImage} from "@/components/common/AnimatedImage";
import {CountdownTextButton} from "@/components/form/CountdownTextButton";
import {useTranslation} from "react-i18next";

// Validation schema
const otpSchema = z.object({
    otp: z
        .string()
        .min(8)
        .max(8)
        .regex(/^\d{8}$/, 'OTP must contain only numbers'),
});

type OtpFormData = z.infer<typeof otpSchema>;

interface LoginOtpVerifyProps {
    otpId: string;
    email: string;
    style?: any;
    onSuccess?: () => void;
    onResendOtp?: () => void;
    onAbort?: () => void;
}

export const LoginOtpVerify: React.FC<LoginOtpVerifyProps> = ({
                                                                  otpId,
                                                                  onSuccess,
                                                                  onResendOtp,
                                                                  style,
                                                                  onAbort,
                                                                  email
                                                              }) => {
    const {theme} = useCurrentTheme();
    const {verifyOTP} = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const {t} = useTranslation('common');

    const {
        control,
        handleSubmit,
        formState: {errors, isValid},
        reset,
    } = useForm<OtpFormData>({
        resolver: zodResolver(otpSchema),
        mode: 'onChange',
        defaultValues: {
            otp: '',
        },
    });

    const styles = createStyles(theme);

    const onSubmit = async (data: OtpFormData) => {
        if (isLoading) return;

        setIsLoading(true);
        setSubmitError(null);

        try {
            await verifyOTP(otpId, data.otp);
            onSuccess?.();
        } catch (error) {
            const errorMessage = error instanceof Error ? t(`verify.error.${error.message}`, error.message) : t('verify.error.invalidOtp');
            setSubmitError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = () => {
        setSubmitError(null);
        reset();
        onResendOtp?.();
    };

    return (
        <Card style={style}>
            <View style={styles.header}>
                <AnimatedImage source={require("@/assets/images/splash-icon.png")} style={styles.image}/>
                <Text style={styles.title}>{t('verify.title')}</Text>
                <Text style={styles.subtitle}>
                    {t('verify.description', {email: email})}
                </Text>
            </View>

            <View style={styles.form}>
                {submitError && (
                    <ErrorMessage message={submitError} style={styles.errorMessage}/>
                )}

                <Controller
                    control={control}
                    name="otp"
                    render={({field: {onChange, onBlur, value}}) => (
                        <TextInput
                            label={t('verify.input.label')}
                            placeholder={t('verify.input.placeholder')}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="number-pad"
                            autoCapitalize="none"
                            autoComplete="one-time-code"
                            textContentType="oneTimeCode"
                            maxLength={8}
                            error={errors.otp?.message}
                            editable={!isLoading}
                            inputStyle={styles.otpInput}
                        />
                    )}
                />

                <SubmitButton
                    title={isLoading ? t('verify.button.loading') : t('verify.button.label')}
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid || isLoading}
                    loading={isLoading}
                    style={styles.submitButton}
                />

                <View style={styles.secondaryActions}>
                    {onAbort && (
                        <TextButton
                            title={t('verify.buttonAbort.label')}
                            onPress={onAbort}
                            disabled={isLoading}
                        />
                    )}
                    {onResendOtp && (
                        <CountdownTextButton
                            key={otpId}
                            countdown={30}
                            title={t('verify.buttonResend.label')}
                            onPress={handleResendOtp}
                            disabled={isLoading}
                        />
                    )}
                </View>
            </View>
        </Card>
    );
};

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        image: {
            width: 100,
            height: 100,
            alignSelf: 'center',
            marginVertical: theme.spacing.md,
        },
        header: {
            marginBottom: theme.spacing.xl,
            alignItems: 'center',
        },
        title: {
            fontSize: theme.fontSize.xxl,
            fontWeight: 'bold',
            color: theme.colors.text,
            marginBottom: theme.spacing.sm,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: theme.fontSize.md,
            color: theme.colors.textSecondary,
            textAlign: 'center',
            lineHeight: theme.fontSize.md * 1.4,
        },
        form: {
            gap: 0,
        },
        errorMessage: {
            marginBottom: theme.spacing.sm,
        },
        otpInput: {
            textAlign: 'center',
            fontSize: theme.fontSize.xxl,
            letterSpacing: 10,
        },
        submitButton: {
            marginTop: theme.spacing.md,
        },
        secondaryActions: {
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            marginTop: theme.spacing.md
        }
    });
